import getPersonaInitialsColorClass from '../utils/getPersonaInitialsColorClass';
import getPersonaSizeClass from '../utils/getPersonaSizeClass';
import PersonaMeetingResponseBadge from './PersonaMeetingResponseBadge';
import React, { RefObject, useRef, useState } from 'react';
import { divProperties, getNativeProps } from 'fabric-utilities';
import { Image, ImageFit, ImageLoadState } from 'fabric-image';
import { PersonaInitialsColor, PersonaProps, PersonaSize } from './Persona.Props';
import { SIZE_TO_PIXELS } from './PersonaConsts';

const styles = require('./Persona.scss');
const classNames = require('classnames/bind').bind(styles);

// The RGB color swatches
const COLOR_SWATCHES_LOOKUP: PersonaInitialsColor[] = [
    PersonaInitialsColor.lightGreen,
    PersonaInitialsColor.lightBlue,
    PersonaInitialsColor.lightPink,
    PersonaInitialsColor.green,
    PersonaInitialsColor.darkGreen,
    PersonaInitialsColor.pink,
    PersonaInitialsColor.magenta,
    PersonaInitialsColor.purple,
    PersonaInitialsColor.black,
    PersonaInitialsColor.teal,
    PersonaInitialsColor.blue,
    PersonaInitialsColor.darkBlue,
    PersonaInitialsColor.orange,
    PersonaInitialsColor.darkRed,
    PersonaInitialsColor.red,
];

const COLOR_SWATCHES_NUM_ENTRIES = COLOR_SWATCHES_LOOKUP.length;
const PERSONA_INITIALS_COLOR = getPersonaInitialsColorClass(styles);
const PERSONA_SIZE = getPersonaSizeClass(styles);
const DATA_TEST_ID = 'Persona';

export interface PersonaState {
    isImageLoaded?: boolean;
}

const getColorFromName = (displayName: string): PersonaInitialsColor => {
    let color = PersonaInitialsColor.blue;
    if (!displayName) {
        return color;
    }

    let hashCode = 0;
    for (let iLen: number = displayName.length - 1; iLen >= 0; iLen--) {
        const ch: number = displayName.charCodeAt(iLen);
        const shift: number = iLen % 8;
        // tslint:disable-next-line:no-bitwise
        hashCode ^= (ch << shift) + (ch >> (8 - shift));
    }

    color = COLOR_SWATCHES_LOOKUP[hashCode % COLOR_SWATCHES_NUM_ENTRIES];

    return color;
};

export const Persona = React.forwardRef((props: PersonaProps, ref: RefObject<HTMLDivElement>) => {
    const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);
    const fallbackRef = useRef<HTMLDivElement>(null);
    const personaRef = ref ?? fallbackRef;
    const {
        className,
        imageClassName,
        size = PersonaSize.regular,
        imageUrl,
        imageAlt = '',
        imageInitials,
        primaryText = '',
        secondaryText,
        tertiaryText,
        optionalText,
        showSecondaryText,
        hidePersonaDetails,
        tabIndex,
        imageShouldFadeIn,
        imageShouldStartVisible,
        meetingResponse,
        onClickPersona,
        initialsColor = getColorFromName(primaryText),
        personaId,
    } = props;

    const onClick = (ev: React.MouseEvent<EventTarget> | React.KeyboardEvent<any>) => {
        if (onClickPersona) {
            ev.stopPropagation();
            onClickPersona(personaRef);
        }
    };

    const onPhotoLoadingStateChange = (loadState: ImageLoadState) => {
        setIsImageLoaded(loadState === ImageLoadState.loaded);
    };

    const divProps = getNativeProps(props, divProperties);
    return (
        <div
            data-focus-id={personaId}
            {...divProps}
            className={classNames(
                'ms-Persona',
                styles.root,
                className,
                PERSONA_SIZE[size],
                showSecondaryText ?? ''
            )}
            tabIndex={tabIndex ?? (onClickPersona ? 0 : undefined)}
            role={onClickPersona ? 'button' : ''}
            ref={personaRef}
            onClick={onClick}
            onKeyDown={e => {
                if (e.key !== 'Enter') {
                    return;
                }
                onClick(e);
            }}
            data-testid={DATA_TEST_ID}>
            {size !== PersonaSize.tiny && (
                <div className={classNames('ms-Persona-imageArea', styles.imageArea)}>
                    {!isImageLoaded && (
                        <div
                            className={classNames(
                                'ms-Persona-initials',
                                styles.initials,
                                PERSONA_INITIALS_COLOR[initialsColor]
                            )}
                            aria-hidden="true">
                            <span>{imageInitials}</span>
                        </div>
                    )}
                    <Image
                        className={classNames('ms-Persona-image', styles.image, imageClassName)}
                        imageFit={ImageFit.cover}
                        src={imageUrl}
                        width={SIZE_TO_PIXELS[size]}
                        height={SIZE_TO_PIXELS[size]}
                        alt={imageAlt}
                        shouldFadeIn={imageShouldFadeIn}
                        shouldStartVisible={imageShouldStartVisible}
                        onLoadingStateChange={onPhotoLoadingStateChange}
                    />
                    <PersonaMeetingResponseBadge
                        personaSize={size}
                        meetingResponse={meetingResponse}
                    />
                </div>
            )}
            {(!hidePersonaDetails || size === PersonaSize.tiny) && (
                <div
                    className={classNames(
                        'ms-Persona-details',
                        'allowTextSelection',
                        styles.details
                    )}>
                    <div className={classNames('ms-Persona-primaryText', styles.primaryText)}>
                        {primaryText}
                    </div>
                    <div className={classNames('ms-Persona-secondaryText', styles.secondaryText)}>
                        {secondaryText}
                    </div>
                    <div className={classNames('ms-Persona-tertiaryText', styles.tertiaryText)}>
                        {tertiaryText}
                    </div>
                    <div className={classNames('ms-Persona-optionalText', styles.optionalText)}>
                        {optionalText}
                    </div>
                    {props.children}
                </div>
            )}
        </div>
    );
});
